import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import TimeRangeFilter from "@/components/admin/TimeRangeFilter";
import {
  DEFAULT_RANGE,
  filterLeadsByRange,
  leadsToCsv,
  downloadCsv,
  RANGE_OPTIONS,
} from "@/lib/leadsFilter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  RefreshCw,
  Download,
  Trash2,
  Mail,
  Phone,
  Globe,
  Clock,
} from "lucide-react";

const STATUS_STYLES = {
  new: "bg-sky-100 text-sky-800 hover:bg-sky-200",
  contacted: "bg-amber-100 text-amber-800 hover:bg-amber-200",
  closed: "bg-neutral-200 text-neutral-700 hover:bg-neutral-300",
};

const STATUS_LABELS = { new: "Neu", contacted: "Kontaktiert", closed: "Erledigt" };

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [selected, setSelected] = useState(new Set());
  const [detailLead, setDetailLead] = useState(null); // lead opened in modal
  const [confirmDelete, setConfirmDelete] = useState(null); // {ids:[...], plural:bool}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredLeads = useMemo(
    () => filterLeadsByRange(leads, range),
    [leads, range],
  );
  const rangeLabel = RANGE_OPTIONS.find((r) => r.key === range)?.label || "";

  const allFilteredSelected =
    filteredLeads.length > 0 && filteredLeads.every((l) => selected.has(l.id));
  const someFilteredSelected =
    !allFilteredSelected && filteredLeads.some((l) => selected.has(l.id));

  const toggleSelectAll = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      filteredLeads.forEach((l) => (checked ? next.add(l.id) : next.delete(l.id)));
      return next;
    });
  };

  const toggleOne = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const exportCsv = () => {
    if (filteredLeads.length === 0) {
      toast.error("Keine Leads im gewählten Zeitraum.");
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    const safeRange = range.replace(/\W/g, "");
    downloadCsv(`leads_${safeRange}_${stamp}.csv`, leadsToCsv(filteredLeads));
    toast.success(`${filteredLeads.length} Leads exportiert.`);
  };

  const cycleStatus = async (lead) => {
    const next =
      lead.status === "new"
        ? "contacted"
        : lead.status === "contacted"
        ? "closed"
        : "new";
    try {
      await api.patch(`/admin/leads/${lead.id}`, null, { params: { status: next } });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: next } : l)));
      if (detailLead?.id === lead.id) setDetailLead({ ...lead, status: next });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const ids = confirmDelete.ids;
    try {
      if (ids.length === 1) {
        await api.delete(`/admin/leads/${ids[0]}`);
      } else {
        await api.post(`/admin/leads/bulk-delete`, { ids });
      }
      setLeads((ls) => ls.filter((l) => !ids.includes(l.id)));
      setSelected((prev) => {
        const next = new Set(prev);
        ids.forEach((i) => next.delete(i));
        return next;
      });
      if (detailLead && ids.includes(detailLead.id)) setDetailLead(null);
      toast.success(
        ids.length === 1
          ? "Lead gelöscht."
          : `${ids.length} Leads gelöscht.`,
      );
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setConfirmDelete(null);
    }
  };

  const selectedCount = selected.size;

  return (
    <AdminShell
      title="Leads"
      subtitle={`Zeitraum „${rangeLabel}" · ${filteredLeads.length} von ${leads.length} Anfragen`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={exportCsv}
            data-testid="admin-leads-export-csv"
            disabled={filteredLeads.length === 0}
            className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            CSV-Export
          </button>
          <button
            onClick={load}
            data-testid="admin-leads-refresh"
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
          >
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </button>
        </div>
      }
    >
      <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
        <TimeRangeFilter
          value={range}
          onChange={setRange}
          testId="admin-leads-range-filter"
        />
        {selectedCount > 0 && (
          <div
            data-testid="admin-leads-bulk-bar"
            className="flex items-center gap-3 rounded-full bg-neutral-950 text-white pl-4 pr-1 py-1"
          >
            <span className="text-sm">
              {selectedCount} ausgewählt
            </span>
            <button
              onClick={() =>
                setConfirmDelete({
                  ids: [...selected],
                  plural: selectedCount > 1,
                })
              }
              data-testid="admin-leads-bulk-delete"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-600 hover:bg-red-700 px-3 py-1.5 text-sm font-medium"
            >
              <Trash2 className="w-4 h-4" />
              Löschen
            </button>
          </div>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
        {loading ? (
          <div className="p-12 grid place-items-center text-neutral-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-12 text-center text-neutral-500" data-testid="admin-leads-empty">
            {leads.length === 0
              ? "Noch keine Anfragen."
              : `Keine Anfragen im Zeitraum „${rangeLabel}".`}
          </div>
        ) : (
          <Table data-testid="admin-leads-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      allFilteredSelected
                        ? true
                        : someFilteredSelected
                        ? "indeterminate"
                        : false
                    }
                    onCheckedChange={(v) => toggleSelectAll(!!v)}
                    aria-label="Alle auswählen"
                    data-testid="admin-leads-select-all"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Leistung</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead className="min-w-[280px]">Nachricht</TableHead>
                <TableHead>Eingegangen</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12 text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.map((l) => {
                const checked = selected.has(l.id);
                return (
                  <TableRow
                    key={l.id}
                    data-testid={`admin-lead-row-${l.id}`}
                    data-state={checked ? "selected" : undefined}
                    className={`group cursor-pointer hover:bg-neutral-50 ${
                      checked ? "bg-sky-50/40" : ""
                    }`}
                    onClick={(e) => {
                      // Don't open when clicking interactive children
                      const tag = e.target.tagName;
                      if (
                        tag === "INPUT" ||
                        tag === "BUTTON" ||
                        tag === "A" ||
                        e.target.closest("button,a,[role=checkbox]")
                      )
                        return;
                      setDetailLead(l);
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => toggleOne(l.id, !!v)}
                        aria-label={`${l.name} auswählen`}
                        data-testid={`admin-lead-select-${l.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <a
                        href={`mailto:${l.email}`}
                        className="text-sky-700 hover:underline"
                      >
                        {l.email}
                      </a>
                    </TableCell>
                    <TableCell>{l.service || "—"}</TableCell>
                    <TableCell className="text-xs text-neutral-600 whitespace-nowrap">
                      {l.source_domain || "—"}
                    </TableCell>
                    <TableCell className="text-neutral-700 max-w-md">
                      <div className="line-clamp-2 text-sm">
                        {l.message || (
                          <span className="text-neutral-400">Keine Nachricht</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-neutral-500 whitespace-nowrap text-xs">
                      {new Date(l.created_at).toLocaleString("de-DE")}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => cycleStatus(l)}
                        data-testid={`admin-lead-status-${l.id}`}
                      >
                        <Badge className={`rounded-full ${STATUS_STYLES[l.status] || ""}`}>
                          {STATUS_LABELS[l.status] || l.status}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() =>
                          setConfirmDelete({ ids: [l.id], plural: false })
                        }
                        data-testid={`admin-lead-delete-${l.id}`}
                        title="Löschen"
                        className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-8 h-8 rounded-full text-neutral-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!detailLead} onOpenChange={(open) => !open && setDetailLead(null)}>
        <DialogContent
          className="max-w-2xl"
          data-testid="admin-lead-detail-dialog"
        >
          {detailLead && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-3 pr-8">
                  <div>
                    <DialogTitle className="text-2xl font-display tracking-tight">
                      {detailLead.name}
                    </DialogTitle>
                    <DialogDescription className="mt-1">
                      Lead-Detail · ID {detailLead.id.slice(0, 8)}
                    </DialogDescription>
                  </div>
                  <Badge
                    className={`rounded-full ${STATUS_STYLES[detailLead.status] || ""}`}
                  >
                    {STATUS_LABELS[detailLead.status] || detailLead.status}
                  </Badge>
                </div>
              </DialogHeader>

              <div className="space-y-5 mt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a
                    href={`mailto:${detailLead.email}`}
                    className="flex items-center gap-3 rounded-2xl border border-neutral-200 p-3 hover:border-neutral-300 hover:bg-neutral-50 transition-colors"
                    data-testid="admin-lead-detail-email"
                  >
                    <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-700 grid place-items-center">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                        E-Mail
                      </div>
                      <div className="text-sm font-medium text-neutral-950 truncate">
                        {detailLead.email}
                      </div>
                    </div>
                  </a>
                  <a
                    href={detailLead.phone ? `tel:${detailLead.phone}` : undefined}
                    className={`flex items-center gap-3 rounded-2xl border border-neutral-200 p-3 transition-colors ${
                      detailLead.phone
                        ? "hover:border-neutral-300 hover:bg-neutral-50"
                        : "opacity-60 cursor-default"
                    }`}
                    data-testid="admin-lead-detail-phone"
                  >
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                        Telefon
                      </div>
                      <div className="text-sm font-medium text-neutral-950">
                        {detailLead.phone || "—"}
                      </div>
                    </div>
                  </a>
                  <div className="flex items-center gap-3 rounded-2xl border border-neutral-200 p-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 grid place-items-center">
                      <Globe className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                        Quelle
                      </div>
                      <div className="text-sm font-medium text-neutral-950 truncate">
                        {detailLead.source_domain || "—"}
                      </div>
                      <div className="text-xs text-neutral-500 truncate">
                        {detailLead.service || "Allgemein"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl border border-neutral-200 p-3">
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 text-neutral-700 grid place-items-center">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                        Eingegangen
                      </div>
                      <div className="text-sm font-medium text-neutral-950">
                        {new Date(detailLead.created_at).toLocaleString("de-DE")}
                      </div>
                      <div className="text-xs text-neutral-500">
                        Sprache: {detailLead.locale?.toUpperCase() || "DE"}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">
                    Nachricht
                  </div>
                  <div
                    className="rounded-2xl bg-neutral-50 border border-neutral-200 p-4 text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed max-h-[40vh] overflow-auto"
                    data-testid="admin-lead-detail-message"
                  >
                    {detailLead.message || (
                      <span className="text-neutral-400">
                        Der Kunde hat keine Nachricht hinterlassen.
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-4 sm:justify-between gap-2 flex-wrap">
                <button
                  onClick={() =>
                    setConfirmDelete({ ids: [detailLead.id], plural: false })
                  }
                  data-testid="admin-lead-detail-delete"
                  className="inline-flex items-center gap-2 rounded-full text-red-600 hover:bg-red-50 px-4 py-2 text-sm font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Lead löschen
                </button>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => cycleStatus(detailLead)}
                    data-testid="admin-lead-detail-cycle"
                    className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
                  >
                    Status weiterschalten
                  </button>
                  <a
                    href={`mailto:${detailLead.email}?subject=${encodeURIComponent(
                      "Re: Ihre Anfrage – Mallorca Digital",
                    )}`}
                    data-testid="admin-lead-detail-reply"
                    className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm hover:bg-black"
                  >
                    <Mail className="w-4 h-4" />
                    Antworten
                  </a>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="admin-leads-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.plural
                ? `${confirmDelete.ids.length} Leads löschen?`
                : "Lead löschen?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Die Anfrage(n)
              werden dauerhaft aus der Datenbank entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-leads-delete-cancel">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="admin-leads-delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
};

export default AdminLeads;
